import { useAccount } from '@azure/msal-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Dropdown, DropdownOption, TextField, Typography } from '@volvo/vcdk-react';
import { Controller, useForm, useWatch, type SubmitHandler } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import type { ISegmentData } from '@/components/modals/unit-registration/AddNewUnitModal';
import type {
  ILocation,
  IMarineForm,
  IMarineUnitRegistration,
  IUnitRegistration,
} from '@/service/vpc/types/IUnitRegistration';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import GeoComboField from '@/components/GeoComboField';
import useAnalytics from '@/contexts/analytics/useAnalytics';
import { addRegisteredUnitToCache, queryKeys } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';
import { account } from '@/utils';

type Props = {
  unitNotExistInProductCenter: boolean;
  segment: Partial<ISegmentData>;
  defaultValues: Partial<IUnitRegistration>;
  defaultValuesMarine: Partial<IMarineForm>;
  onCancel: () => void;
  onNext: (endCustomerUnitId: number) => void;
};

type FormData = {
  vesselName: string;
  vesselId?: 'imoNumber' | 'mmsiNumber';
  vesselValue?: string;
  manufacturer?: string;
  model?: string;
  vesselSegment?: string;
  typeOfVessel?: string;
  street?: string;
  city?: string;
  postal?: string;
  country?: string;
  homePort?: string;
  longitude?: number;
  latitude?: number;
};

const MarineUnitInformationContent: React.FC<Props> = ({
  unitNotExistInProductCenter,
  segment,
  defaultValues,
  defaultValuesMarine,
  onCancel,
  onNext,
}) => {
  const { t } = useTranslation();
  const acc = useAccount();
  const queryClient = useQueryClient();

  const { analyticsEvents } = useAnalytics();
  const browserLanguage = navigator.language || 'en';

  const {
    formState: { isValid },
    control,
    setValue,
    handleSubmit,
  } = useForm<FormData>({
    defaultValues: {
      vesselName: '',
      vesselId: defaultValuesMarine.vesselId,
      vesselValue: defaultValuesMarine.vesselValue,
      manufacturer: defaultValues.manufacturer || '',
      model: defaultValues.model || '',
      vesselSegment: defaultValues.vesselSegment,
      typeOfVessel: defaultValues.typeOfVessel,
      street: '',
      city: '',
      postal: '',
      country: '',
      homePort: '',
      longitude: 0,
      latitude: 0,
    },
  });

  const watchedVesselId = useWatch({
    control: control,
    name: 'vesselId',
  });

  const watchedVesselSegment = useWatch({
    control: control,
    name: 'vesselSegment',
  });

  const { mutateAsync: registerUnit, isPending } = useMutation({
    mutationFn: async (body: IMarineUnitRegistration) => vpcService.Units.registerUnit({ body }),
    onMutate(variables) {
      analyticsEvents.UnitRegistration.unitInformation({
        companyName: variables?.companyName || '',
        unitNotExistInProductCenter: unitNotExistInProductCenter,
        mainSegment: segment.mainSegment || '',
        segment: segment.segment || '',
        segmentDetail: segment.segmentDetail || '',
        engineSerialNumbers: variables.engineSerials.length,
        manufacturer: variables.manufacturer || '',
        model: variables.model || '',
        vesselSegment: variables.vesselSegment || '',
        typeOfVessel: variables.typeOfVessel || '',
      });
    },
    onSuccess({ unitId, unit }) {
      analyticsEvents.UnitRegistration.success({
        companyName: defaultValues.companyName || '',
        unitNotExistInProductCenter: unitNotExistInProductCenter,
        mainSegment: segment.mainSegment || '',
        segment: segment.segment || '',
        segmentDetail: segment.segmentDetail || '',
      });
      addRegisteredUnitToCache(unit);
      void queryClient.invalidateQueries({ queryKey: [queryKeys.getUnitsWithFBB] });
      void queryClient.invalidateQueries({ queryKey: [queryKeys.getUserAuthorizations] });
      onNext(unitId);
    },
    onError(error) {
      // Handle error
      console.error('Error registering unit:', error);
    },
  });

  const renderVesselIdField = () => {
    const vesselId = watchedVesselId;
    if (!vesselId) return null;

    const vesselIdFieldConfig: Record<
      NonNullable<FormData['vesselId']>,
      { name: keyof FormData; label: string; helperText: string }
    > = {
      imoNumber: {
        name: 'vesselValue',
        label: t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.imo_number),
        helperText: t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.imo_number_helperText),
      },
      mmsiNumber: {
        name: 'vesselValue',
        label: t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.mmsi_number),
        helperText: t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.mmsi_number_helperText),
      },
    };

    const { name, label, helperText } = vesselIdFieldConfig[vesselId];

    return (
      <Controller
        key={name}
        name={name as keyof FormData}
        control={control}
        render={({ field }) => (
          <TextField
            disabled
            size="medium"
            label={label}
            className="w-full"
            value={field.value as string}
            onInput={(e) => field.onChange(e.detail.value)}
            helperText={helperText}
          />
        )}
      />
    );
  };

  const getVesselValue = (value: FormData): string => {
    const map: Record<NonNullable<FormData['vesselId']>, string> = {
      imoNumber: value.vesselValue || '',
      mmsiNumber: value.vesselValue || '',
    };
    return value.vesselId ? map[value.vesselId] : '';
  };

  const onSubmit: SubmitHandler<FormData> = async (value) => {
    if (isPending) return;
    if (!acc) return;

    const email = account.retrieveEmail(acc);

    const registrationBody: IMarineUnitRegistration = {
      username: email,
      firstName: acc ? account.retrieveAccountHolderName(acc).firstName : '',
      lastName: acc ? account.retrieveAccountHolderName(acc).surName : '',
      email,
      unitName: value.vesselName || '',
      companyName: '',
      engineSerials: defaultValues.engineSerials || [],
      vesselId: value.vesselId,
      vesselValue: getVesselValue(value),
      vesselSegment: value.vesselSegment,
      typeOfVessel: value.typeOfVessel,
      manufacturer: value.manufacturer,
      model: value.model,
      street: value.street,
      city: value.city,
      postal: value.postal,
      state: '',
      country: value.country,
      homePort: value.homePort,
      longitude: value.longitude,
      latitude: value.latitude,
    };
    await registerUnit(registrationBody);
  };

  const handleUpdateUnitLocation = (location: ILocation) => {
    setValue('street', location.street);
    setValue('city', location.city);
    setValue('postal', location.postal);
    setValue('country', location.country);
    setValue('homePort', location.homePort);
    setValue('longitude', location.longitude);
    setValue('latitude', location.latitude);
  };

  return (
    <>
      <span slot="header">{t(($) => $.components.modal.add_new_unit_modal.unit_information.title)}</span>
      <div className="flex flex-col gap-8">
        <Typography>{t(($) => $.components.modal.add_new_unit_modal.unit_information.description)}</Typography>
        <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
          <TextField
            required
            disabled
            size="medium"
            label={t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.serial_number)}
            className="w-full"
            value={defaultValues.engineSerials ? defaultValues.engineSerials[0] : ''}
            helperText={t(
              ($) => $.components.modal.add_new_unit_modal.marine_unit_information.serial_number_helperText
            )}
          />
          <Controller
            name="vesselName"
            control={control}
            rules={{
              required: true,
              maxLength: 50,
              validate: (value) => value.trim().length > 0,
            }}
            render={({ field }) => (
              <TextField
                required
                size="medium"
                label={t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.vessel_name)}
                className="w-full"
                maxLength={50}
                helperText={t(
                  ($) => $.components.modal.add_new_unit_modal.marine_unit_information.vessel_name_helperText
                )}
                {...field}
              />
            )}
          />
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Controller
              name="vesselId"
              control={control}
              render={({ field }) => (
                <Dropdown
                  disabled
                  size="medium"
                  label={t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.vessel_id)}
                  value={field.value}
                  placement="bottom"
                  className="w-full [&::part(input-wrapper)]:h-10"
                  onChange={(e) => field.onChange(e.detail.value)}
                >
                  <DropdownOption value="imoNumber">IMO number</DropdownOption>
                  <DropdownOption value="mmsiNumber">MMSI number</DropdownOption>
                </Dropdown>
              )}
            />
            {renderVesselIdField()}
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Controller
              name="manufacturer"
              control={control}
              render={({ field }) => (
                <TextField
                  disabled
                  size="medium"
                  label={t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.manufacturer)}
                  className="w-full"
                  value={field.value}
                  onInput={(e) => field.onChange(e.detail.value)}
                  helperText={t(
                    ($) => $.components.modal.add_new_unit_modal.marine_unit_information.manufacturer_helperText
                  )}
                />
              )}
            />
            <Controller
              name="model"
              control={control}
              render={({ field }) => (
                <TextField
                  disabled
                  size="medium"
                  label={t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.model)}
                  className="w-full"
                  value={field.value}
                  onInput={(e) => field.onChange(e.detail.value)}
                  helperText={t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.model_helperText)}
                />
              )}
            />
            <Controller
              name="vesselSegment" // consumerLineOfBusiness
              control={control}
              render={({ field }) => (
                <Dropdown
                  disabled
                  size="medium"
                  label={t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.vessel_segment)}
                  className="w-full"
                  value={field.value}
                  placement="bottom"
                  onChange={(e) => field.onChange(e.detail.value)}
                >
                  <DropdownOption value="Charter">Charter</DropdownOption>
                  <DropdownOption value="Coast Guard and Patrol">Coast Guard and Patrol</DropdownOption>
                  <DropdownOption value="Leisure – Non revenue making">Leisure – Non revenue making</DropdownOption>
                  <DropdownOption value="Passenger transport">Passenger transport</DropdownOption>
                  <DropdownOption value="Short Sea and River Transportation">
                    Short Sea and River Transportation
                  </DropdownOption>
                  <DropdownOption value="Supply Vessels and Offshore Energy">
                    Supply Vessels and Offshore Energy
                  </DropdownOption>
                  <DropdownOption value="Workboats and Tugs">Workboats and Tugs</DropdownOption>
                </Dropdown>
              )}
            />
            <Controller
              name="typeOfVessel" // consumerUnitPurpose
              control={control}
              render={({ field }) => (
                <Dropdown
                  disabled
                  size="medium"
                  label={t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.type_of_vessel)}
                  className="w-full"
                  value={field.value}
                  placement="bottom"
                  onChange={(e) => field.onChange(e.detail.value)}
                >
                  {watchedVesselSegment === 'Charter' && (
                    <>
                      <DropdownOption value="Cruising">Cruising</DropdownOption>
                      <DropdownOption value="Sailing">Sailing</DropdownOption>
                      <DropdownOption value="Sport Fishing">Sport Fishing</DropdownOption>
                      <DropdownOption value="Yacht">Yacht</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Coast Guard and Patrol' && (
                    <>
                      <DropdownOption value="Governmental">Governmental</DropdownOption>
                      <DropdownOption value="Patrol">Patrol</DropdownOption>
                      <DropdownOption value="Pilot">Pilot</DropdownOption>
                      <DropdownOption value="SAR">SAR</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Leisure – Non revenue making' && (
                    <>
                      <DropdownOption value="Cruising">Cruising</DropdownOption>
                      <DropdownOption value="Sailing">Sailing</DropdownOption>
                      <DropdownOption value="Sport Fishing">Sport Fishing</DropdownOption>
                      <DropdownOption value="Yacht">Yacht</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Passenger transport' && (
                    <>
                      <DropdownOption value="Passenger Ferries">Passenger Ferries</DropdownOption>
                      <DropdownOption value="Restaurant Boats">Restaurant Boats</DropdownOption>
                      <DropdownOption value="Sightseeing">Sightseeing</DropdownOption>
                      <DropdownOption value="Taxi">Taxi</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Short Sea and River Transportation' && (
                    <>
                      <DropdownOption value="Barges">Barges</DropdownOption>
                      <DropdownOption value="Dry Cargo">Dry Cargo</DropdownOption>
                      <DropdownOption value="Tankers">Tankers</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Supply Vessels and Offshore Energy' && (
                    <>
                      <DropdownOption value="Crew Boats">Crew Boats</DropdownOption>
                      <DropdownOption value="Daughter Vessels">Daughter Vessels</DropdownOption>
                      <DropdownOption value="MOB – Man Overboard Boat">MOB – Man Overboard Boat</DropdownOption>
                      <DropdownOption value="Supply Vessels">Supply Vessels</DropdownOption>
                      <DropdownOption value="Windfarm Service">Windfarm Service</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Workboats and Tugs' && (
                    <>
                      <DropdownOption value="Commercial Fishing">Commercial Fishing</DropdownOption>
                      <DropdownOption value="Dredgers">Dredgers</DropdownOption>
                      <DropdownOption value="Tugs / Push Boats">Tugs / Push Boats</DropdownOption>
                      <DropdownOption value="Workboats">Workboats</DropdownOption>
                    </>
                  )}
                  <DropdownOption value="Other">Other</DropdownOption>
                </Dropdown>
              )}
            />
          </div>
          <GeoComboField
            size="medium"
            label={t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.home_port)}
            helperText={t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.home_port_helperText)}
            defaultValue=""
            setLocation={(location) => {
              handleUpdateUnitLocation(location);
            }}
            countrycode={'se'} // Default to Sweden, can be dynamic based on user/fleet location
          />
          <Typography>
            <Trans
              i18nKey={($) => $.components.modal.add_new_unit_modal.unit_information.privacy_information}
              components={{
                a: (
                  <a
                    href={`https://api-ecm.volvopenta.com/api/ConsentContent/GetConsentContent?consentID=24&languageCode=${browserLanguage}`}
                    className="underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </Typography>
        </form>
      </div>
      <span slot="footer">
        <div className="flex gap-4">
          <Button variant="secondary" onClick={onCancel} disabled={isPending}>
            {t(($) => $.common.back)}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={isPending || !isValid}
            loading={isPending}
          >
            {t(($) => $.components.modal.add_new_unit_modal.marine_unit_information.add_unit)}
          </Button>
        </div>
      </span>
    </>
  );
};

export default withComponentErrorBoundary(MarineUnitInformationContent);
